import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
  IonButton,
  IonGrid,
  IonCol,
  IonRow,
  IonLabel,
  IonAvatar,
  IonIcon,
  IonList,
  IonFabButton,
  useIonAlert,
  IonItem,
  IonBadge,
  IonCard,
  IonCardContent,
} from "@ionic/react";
import { 
  pencilSharp, 
  camera, 
  businessOutline, 
  mailOutline, 
  personOutline,
  cardOutline,
  logOutOutline,
  trashOutline,
  shieldCheckmarkOutline
} from "ionicons/icons";
import { Redirect, useLocation } from "react-router-dom";
import "../../theme/AppStyles.css";
import { ProfileContext } from "../../context/ProfileContext";
import { useProfileActions } from "../../hooks/useProfileActions";
import { LoadingScreen } from "../../components/LoadingScreen";
import { WebLayout } from "../../components/layouts/WebLayout";
import ProfileEdit from "../Profile_Edit";
import { Capacitor } from "@capacitor/core";
import { Purchases } from "@revenuecat/purchases-capacitor";

const ProfileDesktop: React.FC = () => {
  const { currentUser, authReady } = useContext(AuthContext);
  const { profile, profilePicBadge } = useContext(ProfileContext);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [present] = useIonAlert();
  const location = useLocation();

  const {
    uploadProfilePhoto,
    uploadingPhoto,
    logoutUser,
    deleteUserAccount,
    isAdmin,
    navigateToAdminView,
    navigateToEditProfile,
    navigateToPaywall,
  } = useProfileActions({
    profile,
    currentUser,
    presentAlert: present,
    isDesktopMode: true,
    onEditProfileRequest: () => setIsEditingProfile(true),
  });

  useEffect(() => {
    if (location.pathname.toLowerCase() === "/profile") {
      setIsEditingProfile(false);
    }
  }, [location.key, location.pathname]);

  const handleSubscriptionButton = async () => {
    // Subscribed Schedulers should never see the upsell Paywall.
    // Route to the platform's native subscription management UI instead.
    if (profile.SubscriptionType === "Scheduler") {
      try {
        if (Capacitor.isNativePlatform()) {
          await Purchases.showManageSubscriptions();
        } else {
          present({
            header: "Manage Subscription",
            message:
              "Subscription management is available in the iOS or Android app via your App Store / Google Play account.",
            buttons: ["OK"],
          });
        }
      } catch (err) {
        console.error("[ProfileDesktop] showManageSubscriptions failed:", err);
      }
      return;
    }
    navigateToPaywall("Home.Scheduler");
  };

  if (!authReady) {
    return <LoadingScreen LoadingText="Loading" />;
  }

  if (currentUser.uid === "") {
    return <Redirect to={{ pathname: "/login", search: "?returnUrl=/portal" }} />;
  }

  return (
    <WebLayout title={isEditingProfile ? "Edit Profile" : "Profile"}>
      <div style={{ height: "100%", overflowY: "auto", padding: "16px" }}>
        {isEditingProfile ? (
          <ProfileEdit
            desktopMode={{
              isDesktopMode: true,
              onComplete: () => setIsEditingProfile(false),
            }}
          />
        ) : (
        <IonGrid 
          style={{ maxWidth: "800px", margin: "0 auto", padding: "16px 0" }}
        >
        {/* Profile Card */}
        <IonCard
        >
          <IonCardContent
          >
            <IonRow
            >
              {/* Left Column - Avatar */}
              <IonCol 
                size="4"
                style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}
              >
                <div style={{ position: "relative", marginBottom: "16px" }}>
                  <IonAvatar
                    style={{ width: "150px", height: "150px" }}
                  >
                    <img src={profile.ProfilePicURL} alt="Profile" />
                  </IonAvatar>
                  
                  <IonFabButton
                    size="small"
                    color="medium"
                    onClick={uploadProfilePhoto}
                    disabled={uploadingPhoto}
                    style={{
                      position: "absolute",
                      bottom: "0",
                      right: "0",
                      width: "40px",
                      height: "40px",
                    }}
                  >
                    <IonIcon 
                      icon={camera}
                    />
                    {profilePicBadge === 1 && (
                      <IonBadge
                        color="danger"
                        style={{
                          position: "absolute",
                          top: "-5px",
                          right: "-5px",
                        }}
                      >
                        !
                      </IonBadge>
                    )}
                  </IonFabButton>
                </div>
                
                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={navigateToEditProfile}
                  style={{ width: "100%" }}
                >
                  <IonIcon 
                    slot="start" 
                    icon={pencilSharp}
                  />
                  Edit Profile
                </IonButton>
              </IonCol>

              {/* Right Column - Details */}
              <IonCol 
                size="8"
              >
                <h1 style={{ margin: "0 0 8px", fontSize: "1.8rem" }}>
                  {profile.FirstName} {profile.LastName}
                </h1>
                
                <div style={{ marginBottom: "24px" }}>
                  {/* Occupation & Company */}
                  {(profile.Occupation || profile.Company) && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                      <IonIcon
                        icon={businessOutline}
                        style={{ fontSize: "18px", color: "var(--ion-color-medium)" }}
                      />
                      <span>{profile.Occupation} @ {profile.Company}</span>
                    </div>
                  )}
                  
                  {/* Email */}
                  {profile.Email && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                      <IonIcon
                        icon={mailOutline}
                        style={{ fontSize: "18px", color: "var(--ion-color-medium)" }}
                      />
                      <span>{profile.Email}</span>
                    </div>
                  )}
                  
                  {/* Subscription */}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "16px" }}>
                    <IonIcon
                      icon={cardOutline}
                      style={{ fontSize: "18px", color: "var(--ion-color-medium)" }}
                    />
                    <span>Subscription: <strong>{profile.SubscriptionType}</strong></span>
                    <IonButton
                      size="small"
                      fill="outline"
                      onClick={handleSubscriptionButton}
                      style={{ marginLeft: "auto" }}
                    >
                      {profile.SubscriptionType === "Werker" && "Go Pro"}
                      {profile.SubscriptionType === "Scheduler" && "Downgrade"}
                    </IonButton>
                  </div>
                </div>
              </IonCol>
            </IonRow>
          </IonCardContent>
        </IonCard>

        {/* Bio Section */}
        <IonCard
          style={{ marginTop: "16px" }}
        >
          <IonCardContent
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
              <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "8px" }}>
                <IonIcon 
                  icon={personOutline}
                />
                Bio
              </h3>
              <IonButton
                size="small"
                fill="clear"
                onClick={navigateToEditProfile}
              >
                <IonIcon 
                  slot="start" 
                  icon={pencilSharp}
                />
                Edit Bio
              </IonButton>
            </div>
            
            {profile.UserBio ? (
              <p style={{ whiteSpace: "pre-wrap", lineHeight: "1.6", margin: "0 0 16px" }}>
                {profile.UserBio}
              </p>
            ) : (
              <p style={{ color: "var(--ion-color-medium)", fontStyle: "italic", margin: "0 0 16px" }}>
                About you... Tell others about yourself.
              </p>
            )}
            
            <p style={{ fontSize: "0.85rem", color: "var(--ion-color-medium)", margin: 0 }}>
              <strong>HINT:</strong> Hashtag "#" areas/Counties that you Werk for schedulers to see it when sending out jobs!
            </p>
          </IonCardContent>
        </IonCard>

        {/* Settings Section */}
        <IonCard
          style={{ marginTop: "16px" }}
        >
          <IonCardContent
          >
            <h3 style={{ marginTop: 0 }}>Settings</h3>
            
            <IonList
              lines="full"
            >
              {/* Logout */}
              <IonItem
                button
                onClick={() =>
                  present({
                    header: "Logout",
                    message: "Are you sure you want to logout?",
                    buttons: ["Cancel", { text: "Logout", handler: logoutUser }],
                  })
                }
              >
                <IonIcon 
                  slot="start" 
                  icon={logOutOutline}
                />
                <IonLabel
                >
                  Logout
                </IonLabel>
              </IonItem>

              {/* Delete Account */}
              <IonItem
                button
                onClick={() =>
                  present({
                    header: "!!! Delete Account? This is permanent !!!",
                    message: "This action cannot be undone. All your data will be permanently deleted.",
                    buttons: [
                      "Cancel", 
                      { 
                        text: "Delete", 
                        role: "destructive",
                        handler: deleteUserAccount 
                      }
                    ],
                  })
                }
              >
                <IonIcon 
                  slot="start" 
                  icon={trashOutline} 
                  color="danger"
                />
                <IonLabel 
                  color="danger"
                >
                  Delete Account
                </IonLabel>
              </IonItem>

              {/* Admin View */}
              {isAdmin && (
                <IonItem
                  button
                  onClick={() =>
                    present({
                      header: "Switch to Admin View?",
                      buttons: ["Cancel", { text: "Switch", handler: navigateToAdminView }],
                    })
                  }
                >
                  <IonIcon 
                    slot="start" 
                    icon={shieldCheckmarkOutline}
                  />
                  <IonLabel
                  >
                    Switch to Admin View
                  </IonLabel>
                </IonItem>
              )}
            </IonList>
          </IonCardContent>
        </IonCard>
      </IonGrid>
        )}
      </div>
    </WebLayout>
  );
};

export default ProfileDesktop;
